import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { formatCampaignDateTime } from '../../../../../../types/campaign.utils';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { CesDialogFooterComponent } from '../../../../../ui/dialog-footer/dialog-footer.component';

export interface MergeDialogData {
  assetId: number;
}

export interface MergeDialogResult {
  campaignA: Campaign;
  campaignB: Campaign;
}

@Component({
  selector: 'app-campaign-merge-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoModule, SelectModule, CesDialogFooterComponent],
  templateUrl: './campaign-merge-dialog.component.html',
})
export class CampaignMergeDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<MergeDialogData>>(DynamicDialogConfig);
  private readonly campaignStore = inject(CampaignStore);

  private readonly data: MergeDialogData | undefined = this.config.data;

  readonly selectedAId = signal<number | null>(null);
  readonly selectedBId = signal<number | null>(null);

  readonly ongoingCampaigns = computed(() => {
    const assetId = this.data?.assetId;
    if (!assetId) return [];
    return this.campaignStore
      .entities()
      .filter((c: Campaign) => c.assetId === assetId && c.status === CampaignStatus.ON_GOING)
      .sort((a: Campaign, b: Campaign) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });

  readonly optionsForA = computed(() => this.buildOptions(this.selectedBId()));
  readonly optionsForB = computed(() => this.buildOptions(this.selectedAId()));
  readonly canMerge = computed(() => {
    const a = this.selectedAId();
    const b = this.selectedBId();
    return a !== null && b !== null && a !== b;
  });

  // execute_merge inherits B's timestamp; preview lets the operator see the resulting campaign name before validating.
  readonly mergedPreview = computed(() => {
    const bId = this.selectedBId();
    if (bId === null) return null;
    const b = this.ongoingCampaigns().find((c: Campaign) => c.id === bId);
    return b ? formatCampaignDateTime(b) : null;
  });

  cancel(): void {
    this.ref.close(undefined);
  }

  confirm(): void {
    const aId = this.selectedAId();
    const bId = this.selectedBId();
    if (!aId || !bId) return;
    const campaignA = this.ongoingCampaigns().find((c: Campaign) => c.id === aId);
    const campaignB = this.ongoingCampaigns().find((c: Campaign) => c.id === bId);
    if (!campaignA || !campaignB) return;
    this.ref.close({ campaignA, campaignB });
  }

  private buildOptions(excludeId: number | null) {
    return this.ongoingCampaigns().map((c: Campaign) => ({
      label: this.formatLabel(c),
      value: c.id,
      disabled: c.id === excludeId,
    }));
  }

  private formatLabel(c: Campaign): string {
    let label = formatCampaignDateTime(c);
    if (c.operator) label += ` - ${c.operator}`;
    return label;
  }
}
